/**
 * convoyExceptionnelSlice — Convois exceptionnels (05/02/2026, Q1 retour user prod).
 *
 * Niche narrative T5 : éoliennes, fusée, maisons préfab. Le joueur doit
 * posséder :
 *   1. Le permis convoi exceptionnel (`inv-permit-exceptional` actif).
 *   2. Au moins un tracteur `mantor-heavyhaul-xxl` en flotte.
 *
 * Cycle :
 *   1. Tick mensuel : si conditions remplies → 60 % de chance qu'un email
 *      commercial propose un convoi exceptionnel (3 archétypes : éolienne,
 *      fusée, maison préfab).
 *   2. Le joueur accepte → débite l'intérim ponctuel (3 000-5 000 €), pose
 *      le tracteur + chauffeur en "Mission exceptionnelle", durée 5-10 j.
 *   3. À la fin (tick journalier) :
 *      • 60 % succès complet → 10-25 k€ + +5 réputation + article presse positif
 *      • 25 % aléa modéré (météo +2 j) → -1 500 € de surcoût intérim
 *      • 10 % panne / accident léger → -3 000 € + -2 réputation
 *      •  5 % accident grave → -8 000 € + -5 réputation + article presse négatif
 *
 * Les missions actives vivent dans `convoyExceptionnelMissions` (analogue
 * de `commercialOpportunities`). La résolution + push des messages
 * narratifs se fait dans `tickConvoyExceptionnel`, appelée par
 * `coreSlice.nextDay` après les autres ticks.
 */

const HEAVYHAUL_VEHICLE_ID = 'mantor-heavyhaul-xxl';
const PERMIT_INVESTMENT_ID = 'inv-permit-exceptional';
const MIN_DURATION = 5;
const MAX_DURATION = 10;
const MIN_REWARD = 10000;
const MAX_REWARD = 25000;
const INTERIM_COST_MIN = 3000;
const INTERIM_COST_MAX = 5000;
const PROPOSAL_PROBABILITY_PER_MONTH = 0.6;
const TICK_INTERVAL_DAYS = 30; // 1 tentative tous les 30 jours

const MISSION_ARCHETYPES = [
  {
    id: 'wind-turbine',
    cargo: 'Pale d\'éolienne offshore',
    client: 'NordVent Énergie',
    clientType: 'industriel',
    distance: 420,
    description: 'Pale d\'éolienne 65 m, transport Brest → Lyon avec arrêts nuit obligatoires.',
    pressAngle: 'transition_energy',
  },
  {
    id: 'rocket-stage',
    cargo: 'Étage de fusée Ariane',
    client: 'ArianeSpace Logistics',
    clientType: 'aerospatial',
    distance: 380,
    description: 'Étage central de lanceur Ariane 6, escorte gendarmerie obligatoire, route fermée la nuit.',
    pressAngle: 'aerospace_prestige',
  },
  {
    id: 'prefab-house',
    cargo: 'Maison modulaire 12×4 m',
    client: 'Maisons Préfabri Pro',
    clientType: 'btp',
    distance: 280,
    description: 'Module habitation 18 t pré-assemblé en usine, levage grue à l\'arrivée.',
    pressAngle: 'innovative_housing',
  },
  {
    id: 'industrial-press',
    cargo: 'Presse hydraulique 45T',
    client: 'SteelForge Industries',
    clientType: 'industriel',
    distance: 510,
    description: 'Presse forge 45 t, fragile vibrations, vitesse plafonnée 60 km/h.',
    pressAngle: 'industrial_renaissance',
  },
  {
    id: 'yacht-hull',
    cargo: 'Coque de yacht 22 m',
    client: 'Chantiers Bénéteau Plaisance',
    clientType: 'naval',
    distance: 340,
    description: 'Coque polyester 22 m sortie de chantier La Rochelle vers port Cannes.',
    pressAngle: 'luxury_lifestyle',
  },
];

const INCIDENT_OUTCOMES = [
  { id: 'success', probability: 0.60, label: 'Mission réussie — convoi à l\'heure', repDelta: +5, costDelta: 0, press: 'positive' },
  { id: 'weather', probability: 0.25, label: 'Retard météo (+2 j) — coût intérim majoré', repDelta: -1, costDelta: -1500, press: 'neutral' },
  { id: 'breakdown', probability: 0.10, label: 'Panne mécanique en route', repDelta: -2, costDelta: -3000, press: 'negative_minor' },
  { id: 'accident', probability: 0.05, label: 'Accrochage avec véhicule tiers (responsabilité partielle)', repDelta: -5, costDelta: -8000, press: 'negative_major' },
];

const PRESS_TEMPLATES = {
  positive: {
    title: 'Convoi exceptionnel : SCD signe une démonstration logistique remarquable',
    leadFmt: (cargo, client) => `${client} salue la prestation logistique sans faute d\'SCD pour le transport d\'${cargo}. Une référence pour les futurs grands chantiers du secteur.`,
  },
  neutral: {
    title: 'Convoi exceptionnel SCD retardé par la météo',
    leadFmt: (cargo, client) => `Le transport d\'${cargo} affrété par ${client} a connu un retard de 48 h dû à des conditions météorologiques défavorables. Aucun incident matériel.`,
  },
  negative_minor: {
    title: 'Convoi exceptionnel SCD : panne mécanique sur l\'A6',
    leadFmt: (cargo, client) => `Une panne du tracteur transportant ${cargo} pour ${client} a immobilisé la circulation pendant 4 h sur l\'A6. Le convoi est arrivé à destination en différé.`,
  },
  negative_major: {
    title: 'Accrochage lors d\'un convoi exceptionnel SCD',
    leadFmt: (cargo, client) => `Un accrochage est survenu hier soir entre un convoi exceptionnel SCD transportant ${cargo} pour ${client} et un véhicule léger. Le bilan est matériel, sans blessé grave. Une enquête est en cours.`,
  },
};

function pickRandomArchetype(rng) {
  return MISSION_ARCHETYPES[Math.floor(rng() * MISSION_ARCHETYPES.length)];
}

function pickIncidentOutcome(rng) {
  let r = rng();
  for (const outcome of INCIDENT_OUTCOMES) {
    if (r < outcome.probability) return outcome;
    r -= outcome.probability;
  }
  return INCIDENT_OUTCOMES[0]; // fallback
}

/**
 * 05/02/2026 (retour code review iteration_69) — RNG déterministe pour
 * que la résolution d'une mission donne TOUJOURS le même outcome quand on
 * reprend une sauvegarde, et pour rendre les tests plus stables. Seed
 * fabriqué à partir de l'ID mission (string) et du totalDays courant.
 *
 * Mulberry32 — PRNG 32-bit ultra-simple, suffisant pour un tirage unique.
 */
function makeSeededRng(seedStr) {
  let h = 2166136261; // FNV-1a 32-bit offset basis
  for (let i = 0; i < seedStr.length; i++) {
    h ^= seedStr.charCodeAt(i);
    h = Math.imul(h, 16777619); // FNV prime
  }
  let s = h >>> 0;
  return function rng() {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 05/02/2026 (retour code review iteration_69) — Strict license check :
 * seuls les chauffeurs détenteurs de la qualification 'CE+exceptionnel'
 * peuvent conduire un convoi exceptionnel (formation dédiée requise).
 * Avant ce fix, tout chauffeur CE était éligible — risque sécurité (et
 * narratif) trop élevé pour un transport sensible.
 */
function hasExceptionalLicense(driver) {
  if (!driver) return false;
  if (driver.license === 'CE+exceptionnel') return true;
  return Array.isArray(driver.licenses) && driver.licenses.includes('CE+exceptionnel');
}

function buildOfferEmail(archetype, reward, interimCost, durationDays, totalDays) {
  return {
    senderType: 'client',
    senderName: archetype.client,
    tone: 'formal',
    kind: 'convoy_exceptional_offer',
    subject: `📦 Demande de convoi exceptionnel — ${archetype.cargo}`,
    body:
      `Bonjour,\n\n` +
      `Notre société, **${archetype.client}**, sollicite vos services pour une opération de transport ` +
      `exceptionnel : **${archetype.cargo}** sur un trajet de ~${archetype.distance} km, durée estimée ` +
      `**${durationDays} jours** (incluant nuits obligatoires + escorte).\n\n` +
      `Brief mission : ${archetype.description}\n\n` +
      `**Conditions financières :**\n` +
      `• Forfait mission : **${reward.toLocaleString('fr-FR')} €** (versé à livraison conforme)\n` +
      `• Coût d'intérim spécialisé (second chauffeur + voiture pilote) à votre charge : **${interimCost.toLocaleString('fr-FR')} €** (à régler au démarrage)\n` +
      `• Marge nette estimée : **${(reward - interimCost).toLocaleString('fr-FR')} €**\n\n` +
      `⚠️ **Risques opérationnels** : aléas météo, panne, accidents possibles. Votre réputation est ` +
      `engagée — un convoi exceptionnel réussi est une vitrine, un échec est public.\n\n` +
      `Réponse attendue sous 3 jours.\n\n` +
      `Cordialement,\nDirection logistique — ${archetype.client}`,
    ttlDays: 3,
    choices: [
      { id: 'accept_convoy', label: 'Accepter la mission', tone: 'primary',
        effects: [{ kind: 'convoyExceptionnelAccept', archetypeId: archetype.id, reward, interimCost, durationDays, totalDays }] },
      { id: 'decline_convoy', label: 'Décliner', tone: 'secondary',
        effects: [{ kind: 'reputation', delta: -1 }] },
    ],
  };
}

function buildPressArticle(archetype, outcome, totalDays) {
  const template = PRESS_TEMPLATES[outcome.press] || PRESS_TEMPLATES.neutral;
  return {
    senderType: 'press',
    senderName: 'Logistique Hebdo',
    tone: outcome.press.startsWith('negative') ? 'aggressive' : 'friendly',
    kind: 'press_article',
    subject: template.title,
    body:
      `📰 **${template.title}**\n\n` +
      template.leadFmt(archetype.cargo, archetype.client) +
      `\n\n— Article repris dans la rubrique "Logistique d'exception" du jour.`,
    ttlDays: 14,
  };
}

export function createConvoyExceptionnelSlice(set, get) {
  return {
    convoyExceptionnelMissions: [],
    convoyExceptionnelLastTryDay: 0,
    /**
     * 05/02/2026 — État transitoire consommé par le mini-cinematic UI.
     * Posé par tickConvoyExceptionnel lors d'une completion et remis à
     * null par `dismissConvoyCinematic` une fois la séquence affichée.
     */
    lastConvoyCompletion: null,

    /**
     * Acquitte (dismiss) le cinematic de fin de mission. À appeler depuis
     * le composant `ConvoyMissionCinematic` après lecture.
     */
    dismissConvoyCinematic: () => {
      const completion = get().lastConvoyCompletion;
      set({ lastConvoyCompletion: null });
      // 25/07/2026 (demande user) — convoi livré (succès ou simple retard
      // météo) → vidéo jalon plein écran APRÈS le cinematic + retombée
      // médiatique (l'article presse est poussé par tickConvoyExceptionnel).
      if (completion && (completion.press === 'positive' || completion.press === 'neutral')
        && typeof get().triggerMilestoneVideo === 'function') {
        try {
          get().triggerMilestoneVideo({
            kind: 'convoy-exceptional',
            reason: completion.press === 'positive'
              ? `${completion.cargo} livré sans faute pour ${completion.client} — la presse nationale salue l'exploit logistique`
              : `${completion.cargo} livré pour ${completion.client} malgré un retard météo — couverture presse assurée`,
          });
        } catch (err) { console.warn('[convoy] milestone video failed:', err); }
      }
    },

    /**
     * Démarre une mission convoi exceptionnel (déclenchée par l'effet
     * `convoyExceptionnelAccept` côté messageSlice).
     */
    startConvoyExceptionnel: ({ archetypeId, reward, interimCost, durationDays }) => {
      const state = get();
      const archetype = MISSION_ARCHETYPES.find(a => a.id === archetypeId);
      if (!archetype) return { ok: false, reason: 'unknown_archetype' };

      // Vérifs : tracteur + permis + intérim payable.
      const tractor = (state.vehicles || []).find(
        v => v.modelId === HEAVYHAUL_VEHICLE_ID && v.status === 'idle' && !v.assignedContractId
      );
      const permit = state.activeInvestments && state.activeInvestments[PERMIT_INVESTMENT_ID];
      if (!permit) return { ok: false, reason: 'no_permit' };
      if (!tractor) return { ok: false, reason: 'no_idle_tractor' };
      if ((state.treasury || 0) < interimCost) return { ok: false, reason: 'insufficient_funds' };

      const idleDriver = (state.drivers || []).find(
        d => d.status === 'idle' && !d.onHoliday && !d.assignedVehicleId && hasExceptionalLicense(d)
      );
      if (!idleDriver) return { ok: false, reason: 'no_qualified_driver' };

      const totalDays = state.totalDays || 0;
      const mission = {
        id: `convoy-${totalDays}-${archetype.id}`,
        archetypeId: archetype.id,
        cargo: archetype.cargo,
        client: archetype.client,
        startedOnDay: totalDays,
        endsOnDay: totalDays + durationDays,
        reward,
        interimCost,
        vehicleId: tractor.id,
        driverId: idleDriver.id,
      };

      set({
        convoyExceptionnelMissions: [...(state.convoyExceptionnelMissions || []), mission],
        treasury: (state.treasury || 0) - interimCost,
        vehicles: state.vehicles.map(v =>
          v.id === tractor.id
            ? { ...v, status: 'active', assignedDriverId: idleDriver.id, convoyExceptionnelMissionId: mission.id }
            : v
        ),
        drivers: state.drivers.map(d =>
          d.id === idleDriver.id
            ? { ...d, status: 'active', assignedVehicleId: tractor.id, convoyExceptionnelMissionId: mission.id }
            : d
        ),
      });

      if (typeof get().pushCustomMessage === 'function') {
        get().pushCustomMessage({
          senderType: 'advisor',
          senderName: 'Direction opérations',
          tone: 'formal',
          kind: 'info',
          subject: `Convoi exceptionnel ${archetype.cargo} — départ confirmé`,
          body:
            `Mission **${archetype.cargo}** pour **${archetype.client}** lancée. Tracteur Heavyhaul ` +
            `mobilisé jusqu'au **J${mission.endsOnDay}** (${durationDays} jours).\n\n` +
            `Intérim spécialisé débité : ${interimCost.toLocaleString('fr-FR')} €.\n` +
            `Forfait mission attendu à livraison : ${reward.toLocaleString('fr-FR')} €.\n\n` +
            `Suivi quotidien dans la flotte (statut "Convoi exceptionnel").`,
          ttlDays: durationDays + 2,
        });
      }
      return { ok: true, missionId: mission.id };
    },

    /**
     * Tick journalier — résout les missions terminées + propose
     * sporadiquement de nouvelles missions.
     *
     * 05/02/2026 (retour code review iteration_69) :
     *   • RNG seedé sur (mission.id + totalDays) pour outcomes
     *     reproductibles entre rechargements de save.
     *   • Publie `lastConvoyCompletion` (transient state) consommé par
     *     `ConvoyMissionCinematic` pour déclencher l'animation cinétique
     *     de fin de mission (vitrine prestige).
     */
    tickConvoyExceptionnel: () => {
      const state = get();
      const totalDays = state.totalDays || 0;
      const missions = state.convoyExceptionnelMissions || [];

      // 1) Résoudre les missions qui se terminent.
      const completing = missions.filter(m => totalDays >= m.endsOnDay);
      const stillActive = missions.filter(m => totalDays < m.endsOnDay);

      let treasury = state.treasury || 0;
      let reputation = state.reputation || 50;
      let vehicles = state.vehicles || [];
      let drivers = state.drivers || [];
      const pendingMessages = [];
      let lastCompletionForCinematic = null;

      completing.forEach((mission) => {
        const archetype = MISSION_ARCHETYPES.find(a => a.id === mission.archetypeId);
        if (!archetype) return;
        // Outcome RNG seedé sur (mission.id, totalDays).
        const rng = makeSeededRng(`${mission.id}:${totalDays}`);
        const outcome = pickIncidentOutcome(rng);

        const netReward = mission.reward + outcome.costDelta;
        treasury += netReward;
        reputation = Math.max(0, Math.min(100, reputation + outcome.repDelta));

        // Libérer véhicule + chauffeur.
        vehicles = vehicles.map(v =>
          v.id === mission.vehicleId
            ? { ...v, status: 'idle', assignedDriverId: null, convoyExceptionnelMissionId: null }
            : v
        );
        drivers = drivers.map(d =>
          d.id === mission.driverId
            ? { ...d, status: 'idle', assignedVehicleId: null, convoyExceptionnelMissionId: null }
            : d
        );

        // Email de bilan + article presse.
        pendingMessages.push({
          senderType: 'advisor',
          senderName: 'Direction opérations',
          tone: outcome.repDelta < 0 ? 'aggressive' : 'friendly',
          kind: 'info',
          subject: `Convoi exceptionnel ${archetype.cargo} — bilan`,
          body:
            `**${outcome.label}**\n\n` +
            `• Forfait perçu : ${mission.reward.toLocaleString('fr-FR')} €\n` +
            `• Aléa coût : ${outcome.costDelta.toLocaleString('fr-FR')} €\n` +
            `• **Net : ${netReward.toLocaleString('fr-FR')} €**\n` +
            `• Réputation : ${outcome.repDelta > 0 ? '+' : ''}${outcome.repDelta}\n\n` +
            `Tracteur et chauffeur libérés.`,
          ttlDays: 14,
        });

        if (PRESS_TEMPLATES[outcome.press]) {
          pendingMessages.push(buildPressArticle(archetype, outcome, totalDays));
        }

        // 05/02/2026 — Garde la dernière completion pour déclencher le
        // mini-cinematic dans l'UI. On ne garde que la première (s'il y
        // en a plusieurs, l'UI les fera défiler après dismiss).
        if (!lastCompletionForCinematic) {
          lastCompletionForCinematic = {
            missionId: mission.id,
            cargo: archetype.cargo,
            client: archetype.client,
            outcome: outcome.id,
            outcomeLabel: outcome.label,
            netReward,
            grossReward: mission.reward,
            costDelta: outcome.costDelta,
            repDelta: outcome.repDelta,
            press: outcome.press,
            triggeredOnDay: totalDays,
          };
        }
      });

      // 2) Génération auto : DÉSACTIVÉE depuis 05/02/2026.
      // « Les contrats Convoi exceptionnel passent forcément par Appel
      //  d'Offres » (retour user prod). Le tick résout désormais
      //  UNIQUEMENT les missions actives ; les nouvelles missions
      //  apparaissent dans la page AO via `data/exceptionalTenders.js`,
      //  filtrées par possession du tracteur Heavyhaul XXL + permis.
      const lastTry = state.convoyExceptionnelLastTryDay || 0;
      const newProposals = []; // toujours vide depuis ce changement

      const stateUpdate = {
        convoyExceptionnelMissions: stillActive,
        treasury,
        reputation,
        vehicles,
        drivers,
      };
      if (totalDays - lastTry >= TICK_INTERVAL_DAYS) {
        stateUpdate.convoyExceptionnelLastTryDay = totalDays;
      }
      if (lastCompletionForCinematic) {
        stateUpdate.lastConvoyCompletion = lastCompletionForCinematic;
      }
      set(stateUpdate);

      // Push messages APRÈS set() pour éviter races.
      if (typeof get().pushCustomMessage === 'function') {
        [...pendingMessages, ...newProposals].forEach(msg => {
          try { get().pushCustomMessage(msg); } catch (err) { console.warn('[convoy] push msg failed:', err); }
        });
      }

      return { completed: completing.length, proposals: newProposals.length };
    },

    getConvoyExceptionnelMissions: () => get().convoyExceptionnelMissions || [],
  };
}

export const _CONVOY_INTERNAL = {
  MISSION_ARCHETYPES,
  INCIDENT_OUTCOMES,
  PRESS_TEMPLATES,
  HEAVYHAUL_VEHICLE_ID,
  PERMIT_INVESTMENT_ID,
  TICK_INTERVAL_DAYS,
};
